// ADR 15.9.26aa D1 — what a rejected generation is, as the phones would name it.
//
// Extracted from `analytics.ts` by ADR 15.9.26ad, which took that file to its
// 300-line budget: the fold is the web's half of rust-core's `api_error_label`
// and stands on its own; `analytics.ts` re-exports it so every importer and
// the pin in `__tests__/analytics.test.ts` are unchanged.

import { ApiError } from '../../api/http'
import { MalformedVocabBody } from '../../api/vocab'
import { RequestTimeoutError } from '../../offline/networkFailure'

/**
 * The web's half of rust-core's `api_error_label` (`api_error/mod.rs:67`),
 * spelled identically so the panel's `reason` breakdown pools three clients on
 * one row. Closed at twelve and pinned by `__tests__/analytics.test.ts`.
 */
export type VocabFailureReason =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'bad_request'
  | 'server'
  | 'safety_input_flagged'
  | 'safety_output_revoked'
  | 'parse'
  | 'network'
  | 'timeout' // ADR 2.10.26s §3 — `ApiError::Timeout`'s slug, now the web has a ceiling.

/** `{ reason, status }` — `status` is `0` on the transport rows, the phones' value. */
export interface VocabFailure {
  reason: VocabFailureReason
  status: number
}

/** The exact statuses — rust-core's `from_status` arms, one row each. */
const REASON_BY_STATUS: Record<number, VocabFailureReason> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  429: 'rate_limited',
}

/** The 422 fold, `safety_error_from_422`'s: the typed code, else its residual. */
function safetyReason(code: string | undefined): VocabFailureReason {
  if (code === 'safety_input_flagged' || code === 'safety_output_revoked') return code
  return 'server'
}

/**
 * The status-bearing rows, split from the transport rows the way rust-core
 * split `http_label` from `transport_label` — the fold is eleven arms against
 * a complexity budget of ten. Every residual 4xx is `bad_request` (the
 * `400..=499` arm — 402, 415 and 400 alike, so a 402 is never `network`).
 */
function httpReason(error: ApiError): VocabFailureReason {
  const exact = REASON_BY_STATUS[error.status]
  if (exact !== undefined) return exact
  if (error.status === 422) return safetyReason(error.code)
  if (error.status >= 400 && error.status < 500) return 'bad_request'
  return 'server'
}

/**
 * A `MalformedVocabBody` is `parse`, a `RequestTimeoutError` is `timeout`
 * (ADR 2.10.26s §3 — ahead of the `network` arm it would otherwise fall
 * through to), and any other thrown value — a fetch that
 * never got a response — is `network`; both carry `status: 0`
 * (`api_error_status_code`'s transport value) so the `status` breakdown
 * pools on `0` rather than splitting on `undefined`.
 */
export function vocabFailureReason(error: unknown): VocabFailure {
  if (error instanceof ApiError) return { reason: httpReason(error), status: error.status }
  if (error instanceof MalformedVocabBody) return { reason: 'parse', status: 0 }
  if (error instanceof RequestTimeoutError) return { reason: 'timeout', status: 0 }
  return { reason: 'network', status: 0 }
}
